/**
 * Pure diary geometry + drag engine. No React, no Date, no DOM — it maps minutes-from-midnight to
 * pixels and back, snaps and clamps a dragged booking, and lanes overlapping bookings within a
 * chair column. Keeping it pure is what makes the <50ms drag interaction budget measurable in CI
 * (test/budget) and keeps every pointer-move cheap (brief WS2: <50ms on a 5-year-old iPad).
 */

/** 72px per hour — matches the diary design's hour grid. */
export const HOUR_PX = 72;
export const DEFAULT_START_MIN = 9 * 60;
export const DEFAULT_END_MIN = 18 * 60;
/** Drag snaps to 5-minute increments; rendering is exact. */
export const SNAP_MIN = 5;
const CLAMP_MIN = 7 * 60;
const CLAMP_MAX = 21 * 60;

export interface DiaryGeometry {
  startMin: number;
  endMin: number;
  hourPx: number;
}

export interface Span {
  startMin: number;
  endMin: number;
}

export interface BlockBox {
  top: number;
  height: number;
}

export function makeGeometry(startMin: number, endMin: number, hourPx = HOUR_PX): DiaryGeometry {
  return { startMin, endMin, hourPx };
}

export function gridHeight(g: DiaryGeometry): number {
  return ((g.endMin - g.startMin) / 60) * g.hourPx;
}

export function yForMinute(min: number, g: DiaryGeometry): number {
  return ((min - g.startMin) / 60) * g.hourPx;
}

export function minuteForY(y: number, g: DiaryGeometry): number {
  return g.startMin + (y / g.hourPx) * 60;
}

export function blockBox(span: Span, g: DiaryGeometry): BlockBox {
  return {
    top: yForMinute(span.startMin, g),
    height: (Math.max(1, span.endMin - span.startMin) / 60) * g.hourPx,
  };
}

export function snapMinute(min: number, snap: number = SNAP_MIN): number {
  return Math.round(min / snap) * snap;
}

/** Shift a span while preserving its duration, snapping the start and keeping it on the grid. */
export function dragSpan(
  span: Span,
  deltaY: number,
  g: DiaryGeometry,
  snap: number = SNAP_MIN,
): Span {
  const duration = span.endMin - span.startMin;
  const deltaMin = (deltaY / g.hourPx) * 60;
  let startMin = snapMinute(span.startMin + deltaMin, snap);
  startMin = Math.max(g.startMin, Math.min(startMin, g.endMin - duration));
  return { startMin, endMin: startMin + duration };
}

/**
 * Derive the visible window from the day's bookings + working hours, clamped to a sane band and
 * floored/ceiled to whole hours. Defaults to 9–18 when there's nothing to show.
 */
export function computeRange(
  spans: Span[],
  work?: { startMin: number; endMin: number },
  hourPx = HOUR_PX,
): DiaryGeometry {
  let lo = work ? work.startMin : DEFAULT_START_MIN;
  let hi = work ? work.endMin : DEFAULT_END_MIN;
  for (const s of spans) {
    if (s.startMin < lo) lo = s.startMin;
    if (s.endMin > hi) hi = s.endMin;
  }
  lo = Math.max(CLAMP_MIN, Math.floor(lo / 60) * 60);
  hi = Math.min(CLAMP_MAX, Math.ceil(hi / 60) * 60);
  if (hi - lo < 60) hi = lo + 60;
  return makeGeometry(lo, hi, hourPx);
}

/**
 * Find open gaps in a chair's day big enough to offer to the queue. `spans` must be start-sorted;
 * gaps are clipped to [windowStart, windowEnd] and only those at/after `notBefore` are returned
 * (we never offer a slot in the past). Pure — exercised by the interaction budget alongside layout.
 */
export function findGaps(
  spans: Span[],
  windowStart: number,
  windowEnd: number,
  minGapMin: number,
  notBefore = -Infinity,
): Span[] {
  const gaps: Span[] = [];
  let cursor = windowStart;
  for (const s of spans) {
    if (s.startMin > cursor) {
      const start = Math.max(cursor, windowStart);
      const end = Math.min(s.startMin, windowEnd);
      if (end - start >= minGapMin && end > notBefore) {
        gaps.push({ startMin: Math.max(start, notBefore), endMin: end });
      }
    }
    cursor = Math.max(cursor, s.endMin);
  }
  if (windowEnd - cursor >= minGapMin && windowEnd > notBefore) {
    gaps.push({ startMin: Math.max(cursor, notBefore), endMin: windowEnd });
  }
  return gaps;
}

export interface Laned {
  lane: number;
  laneCount: number;
}

/**
 * Greedily lane overlapping spans within one chair column so a double-booking (or a processing
 * overlap) renders side-by-side rather than stacked. Items must be passed in start order; the
 * returned array is index-aligned with the input.
 */
export function assignLanes(spans: Span[]): Laned[] {
  const laneEnds: number[] = [];
  const lanes: number[] = new Array(spans.length).fill(0);
  for (let i = 0; i < spans.length; i++) {
    const s = spans[i]!;
    let placed = -1;
    for (let l = 0; l < laneEnds.length; l++) {
      if (laneEnds[l]! <= s.startMin) {
        placed = l;
        break;
      }
    }
    if (placed === -1) {
      placed = laneEnds.length;
      laneEnds.push(s.endMin);
    } else {
      laneEnds[placed] = s.endMin;
    }
    lanes[i] = placed;
  }
  // Resolve laneCount per overlapping cluster: simplest correct answer is the global max for the
  // column (clusters are tiny in practice). This keeps widths consistent down a chair.
  const laneCount = Math.max(1, laneEnds.length);
  return lanes.map((lane) => ({ lane, laneCount }));
}
